import React, { useEffect, useState, useRef } from "react";
import env from "../../../../../config/env";
import Icon from "../../../../YourAIAgents/components/components/icon";
import Nango from "@nangohq/frontend";
import microsoftteamIcon from "../../../../../assets/Images/MicrosoftTeams.png";
import { useUserInfo } from "../../../../../context/user";
import CommonModal from "../../../../Settings/components/ResetContext/components/DumpModal";
import { Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import {
  useCreateNangoConnectSession,
  useStoreNangoSession,
} from "../../../../../queries/integrations";

const IntegrationModal = ({
  isOpen,
  onClose,
  requiredIntegrations,
  onAllConnected,
  rqIntegration,
  integration_id,
}) => {
  const [userInfo, , , , userActiveLocation] = useUserInfo();
  const [waiting] = useState(false);
  const [loading, setLoading] = useState(false);
  // Use useRef to persist the interval reference across re-renders
  const connectionIntervalRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const NANGO_API_HOST = env.NANGO_API_HOST || "https://api.nango.dev";
  const NANGO_CONNECT_UI_HOST =
    env.NANGO_CONNECT_UI_HOST || "https://connect.nango.dev";
  const hasConnectedRef = useRef(false);

  const nango = new Nango({
    host: NANGO_API_HOST,
    apiURL: NANGO_API_HOST,
  });

  const createSessionMutation = useCreateNangoConnectSession();
  const storeSessionMutation = useStoreNangoSession();

  const handleInstallApp = (provider) => {
    setLoading(true);
    let installUrl = "";
    if (provider === "microsoft-teams") {
      installUrl = process.env.REACT_APP_TEAMS_URL;
    }
    window.open(installUrl, "_blank");
    setLoading(false);
  };

  useEffect(() => {
    if (!isOpen) {
      // Reset loading state when modal is closed
      setLoading(false);
      if (connectionIntervalRef.current) {
        clearInterval(connectionIntervalRef.current);
        connectionIntervalRef.current = null;
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const launchOAuthPopup = async (provider) => {
    try {
      const sessionResponse = await createSessionMutation.mutateAsync({
        userInfo,
        allowedIntegrations: [provider],
      });

      if (
        !sessionResponse?.success ||
        !sessionResponse?.data?.token
      ) {
        throw new Error(
          sessionResponse?.message ||
            sessionResponse?.error ||
            "Failed to get session token",
        );
      }

      const sessionToken = sessionResponse.data.token;
      const connect = nango.openConnectUI({
        baseURL: NANGO_CONNECT_UI_HOST,
        apiURL: NANGO_API_HOST,
        onEvent: async (event) => {
          if (event.type === "close") {
          } else if (event.type === "connect") {
            try {
              const { connectionId } = event.payload;
              const sendConnectionToBackend = await storeSessionMutation.mutateAsync({
                provider,
                connection_id: connectionId,
                profile_id: userActiveLocation?.id,
                user_id: userInfo?.userId,
                agency_id: userActiveLocation?.agency_id,
                ...(integration_id ? { integration_id } : {}),
              });
              if (sendConnectionToBackend?.success) {
                hasConnectedRef.current = true;
                onAllConnected();
                onClose();
              } else {
              }
            } catch (err) {}
          }
        },
      });

      connect.setSessionToken(sessionToken);
    } catch (err) {
      console.error("Nango connection error:", err);
    }
  };

  return (
    <CommonModal title="" isOpen={isOpen} onClose={handleClose} showAlert={false}>
      <div>
        {waiting && (
          <div className="absolute inset-0 bg-card/90 rounded-[15px] flex flex-col items-center justify-center z-10">
            <Loader2 className="w-8 h-8 animate-spin text-foreground mb-3" />
          </div>
        )}

        <h1 className="text-center text-[16px] font-semibold m-0 text-foreground">
          {`Connect to ${rqIntegration === "slack" ? "Slack" : "Microsoft Teams"}`}
        </h1>
        <p className="text-center text-[13px] mt-2 text-muted-foreground">
          {rqIntegration === "slack"
            ? "To create this task, connect to your Slack Workplace"
            : "To create this task, first follow the install app instructions and then you will be able to connect to Microsoft Teams."}
        </p>

        <div className="mt-4">
          <div className="flex items-center justify-between mt-3 border border-border rounded-md px-2.5 py-[7px]">
            <div className="flex items-center">
              {rqIntegration === "slack" ? (
                <Icon id="slack" width={30} height={30} />
              ) : (
                <img
                  src={microsoftteamIcon}
                  alt={rqIntegration}
                  className="w-10 h-10"
                />
              )}
              <p className="text-[14px] font-semibold m-0 pl-2.5 text-foreground">
                {rqIntegration === "slack" ? "Slack" : "Microsoft Teams"}
              </p>
            </div>

            {rqIntegration === "microsoft-teams" && (
              <Button
                type="button"
                style={{ width: 134, height: 42 }}
                onClick={() => handleInstallApp("microsoft-teams")}
                disabled={loading}
              >
                {loading ? "Installing..." : "Install App"}
              </Button>
            )}

            <Button
              type="button"
              variant={rqIntegration === "slack" ? "default" : "outline"}
              style={{ width: 134, height: 42 }}
              onClick={() => launchOAuthPopup(rqIntegration)}
              disabled={loading}
            >
              {loading ? "Connecting..." : "Connect"}
            </Button>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

export default IntegrationModal;
